import { DEFAULT_WEEKLY_TIMELINE_WEEKS } from '../../../utils/chartTimeline';
import { resolveTaskStatus } from '../helpers';
import { getTaskPayoutDateKey, toLocalDateKey } from './taskPayoutDate';

/** Default week count for activity calendar (matches 3-month chart preset). */
export const STATS_WEEK_COUNT = 13;

function toDateKey(value) {
    return toLocalDateKey(value);
}

function addToMap(map, dateKey, delta = 1) {
    if (!dateKey) return;
    map.set(dateKey, (map.get(dateKey) || 0) + delta);
}

function startOfWeekSunday(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    while (d.getDay() !== 0) {
        d.setDate(d.getDate() - 1);
    }
    return d;
}

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    d.setHours(0, 0, 0, 0);
    return d;
}

function todayLocal() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function formatWeekLabel(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonthDay(date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function buildWeekGrid(weekCount = DEFAULT_WEEKLY_TIMELINE_WEEKS) {
    const end = todayLocal();
    const totalDays = weekCount * 7;
    const rangeStart = addDays(end, -(totalDays - 1));
    const gridStart = startOfWeekSunday(rangeStart);
    return { end, rangeStart, gridStart, weekCount };
}

function sumMapInRange(map, rangeStart, end) {
    let total = 0;
    map.forEach((value, key) => {
        const day = new Date(`${key}T12:00:00`);
        if (Number.isNaN(day.getTime())) return;
        if (day >= rangeStart && day <= end) {
            total += value;
        }
    });
    return total;
}

function countActiveDaysInRange(map, rangeStart, end) {
    let activeDays = 0;
    map.forEach((count, key) => {
        if (!count) return;
        const day = new Date(`${key}T12:00:00`);
        if (Number.isNaN(day.getTime())) return;
        if (day >= rangeStart && day <= end) activeDays += 1;
    });
    return activeDays;
}

/** Collect per-day activity counts from tasks (submissions, milestones). */
export function collectActivityByDay(tasks = [], role = 'employee') {
    const counts = new Map();

    tasks.forEach((task) => {
        const status = resolveTaskStatus(task);

        (task.submissions || []).forEach((submission) => {
            addToMap(counts, toDateKey(submission.submittedAt));
        });

        if (role === 'employee') {
            if (['active', 'submitted'].includes(status)) {
                addToMap(counts, toDateKey(task.updatedAt || task.createdAt));
            }
            if (status === 'pending') {
                addToMap(counts, toDateKey(task.createdAt));
            }
        } else {
            addToMap(counts, toDateKey(task.createdAt));
            if (['released', 'approved', 'submitted'].includes(status)) {
                addToMap(counts, toDateKey(task.updatedAt));
            }
        }
    });

    return counts;
}

/** Per-day earned/released amounts (employee = received, company = paid out). */
export function collectEarningsByDay(tasks = [], role = 'employee') {
    const amounts = new Map();

    tasks.forEach((task) => {
        const status = resolveTaskStatus(task);
        const paymentStatus = String(task?.payment?.status || '').toLowerCase();
        const isPaidOut =
            status === 'released' ||
            status === 'approved' ||
            paymentStatus === 'released';
        if (!isPaidOut) return;

        const amount = Number(task.amount) || 0;
        if (amount <= 0) return;

        const day = getTaskPayoutDateKey(task);
        if (!day) return;

        amounts.set(day, (amounts.get(day) || 0) + amount);
    });

    return amounts;
}

/** GitHub-style grid: columns = weeks, rows = Sun–Sat. */
export function buildActivityCalendar(
    tasks = [],
    role = 'employee',
    weekCount = STATS_WEEK_COUNT,
) {
    const activityByDay = collectActivityByDay(tasks, role);
    const { end, rangeStart, gridStart } = buildWeekGrid(weekCount);

    const weeks = [];
    let maxCount = 0;
    let activeDays = 0;
    let totalEvents = 0;

    for (let w = 0; w < weekCount; w += 1) {
        const weekStart = addDays(gridStart, w * 7);
        const days = [];

        for (let dow = 0; dow < 7; dow += 1) {
            const cellDate = addDays(weekStart, dow);
            const inRange = cellDate >= rangeStart && cellDate <= end;
            const key = toDateKey(cellDate);
            const count = inRange && key ? activityByDay.get(key) || 0 : 0;

            if (inRange && count > 0) {
                activeDays += 1;
                totalEvents += count;
            }
            if (count > maxCount) maxCount = count;

            days.push({
                date: cellDate,
                dateKey: key,
                count,
                inRange,
                label: formatMonthDay(cellDate),
            });
        }

        weeks.push({ weekStart, days });
    }

    const monthLabels = [];
    let lastMonth = -1;
    weeks.forEach((week, index) => {
        const m = week.weekStart.getMonth();
        if (m !== lastMonth) {
            monthLabels.push({
                weekIndex: index,
                label: week.weekStart.toLocaleDateString('en-US', { month: 'short' }),
            });
            lastMonth = m;
        }
    });

    return {
        weeks,
        monthLabels,
        maxCount,
        activeDays,
        totalEvents,
        rangeStart,
        rangeEnd: end,
    };
}

export function levelForCount(count, maxCount) {
    if (!count || maxCount <= 0) return 0;
    const ratio = count / maxCount;
    if (ratio <= 0.25) return 1;
    if (ratio <= 0.5) return 2;
    if (ratio <= 0.75) return 3;
    return 4;
}

/** Weekly earnings for the chart. */
export function buildWeeklyEarningsSeries(
    tasks = [],
    role = 'employee',
    weekCount = DEFAULT_WEEKLY_TIMELINE_WEEKS,
) {
    const earningsByDay = collectEarningsByDay(tasks, role);
    const { end, gridStart } = buildWeekGrid(weekCount);

    const series = [];
    let maxAmount = 0;
    let totalAmount = 0;

    for (let w = 0; w < weekCount; w += 1) {
        const weekStart = addDays(gridStart, w * 7);
        const weekEnd = addDays(weekStart, 6);
        let amount = 0;

        for (let d = 0; d < 7; d += 1) {
            const cell = addDays(weekStart, d);
            if (cell > end) break;
            const key = toDateKey(cell);
            amount += key ? earningsByDay.get(key) || 0 : 0;
        }

        if (amount > maxAmount) maxAmount = amount;
        totalAmount += amount;

        const labelEnd = weekEnd > end ? end : weekEnd;

        series.push({
            weekStart,
            weekEnd: labelEnd,
            amount,
            label: formatWeekLabel(labelEnd),
        });
    }

    return { series, maxAmount, totalAmount };
}

/** Data shape for TransactionVolumeChart: weekly points. */
export function buildWeeklyChartData(
    tasks = [],
    role = 'employee',
    weekCount = DEFAULT_WEEKLY_TIMELINE_WEEKS,
) {
    const earnings = buildWeeklyEarningsSeries(tasks, role, weekCount);
    const activityByDay = collectActivityByDay(tasks, role);
    const { end, gridStart } = buildWeekGrid(weekCount);

    return earnings.series.map((week, w) => {
        const weekStart = addDays(gridStart, w * 7);
        let workEvents = 0;
        for (let d = 0; d < 7; d += 1) {
            const cell = addDays(weekStart, d);
            if (cell > end) break;
            const key = toDateKey(cell);
            workEvents += key ? activityByDay.get(key) || 0 : 0;
        }
        return {
            date: toLocalDateKey(week.weekEnd || weekStart),
            labelDate: toLocalDateKey(week.weekEnd || weekStart),
            sent: workEvents,
            received: week.amount || 0,
        };
    });
}

/** Daily payout/earnings chart — one point per day, includes today. */
export function buildDailyPayoutChartData(
    tasks = [],
    role = 'employee',
    dayCount = 30,
) {
    const earningsByDay = collectEarningsByDay(tasks, role);
    const activityByDay = collectActivityByDay(tasks, role);
    const end = todayLocal();
    const rangeStart = addDays(end, -(dayCount - 1));

    const buckets = [];
    for (let i = 0; i < dayCount; i += 1) {
        const cell = addDays(rangeStart, i);
        if (cell > end) break;
        const key = toLocalDateKey(cell);
        buckets.push({
            date: key,
            labelDate: key,
            sent: key ? activityByDay.get(key) || 0 : 0,
            received: key ? earningsByDay.get(key) || 0 : 0,
        });
    }
    return buckets;
}

export function computeDailyStatsSummary(
    tasks = [],
    role = 'employee',
    dayCount = 30,
) {
    const activityByDay = collectActivityByDay(tasks, role);
    const earningsByDay = collectEarningsByDay(tasks, role);
    const end = todayLocal();
    const rangeStart = addDays(end, -(dayCount - 1));

    const activeDays = countActiveDaysInRange(activityByDay, rangeStart, end);
    const totalEvents = sumMapInRange(activityByDay, rangeStart, end);
    const totalEarned = sumMapInRange(earningsByDay, rangeStart, end);

    const submissions = tasks.reduce(
        (sum, task) => sum + (Array.isArray(task.submissions) ? task.submissions.length : 0),
        0,
    );

    return {
        activeDays,
        totalEvents,
        totalEarned,
        submissions,
    };
}

export function computeStatsSummary(
    tasks = [],
    role = 'employee',
    weekCount = DEFAULT_WEEKLY_TIMELINE_WEEKS,
) {
    const activityByDay = collectActivityByDay(tasks, role);
    const earningsByDay = collectEarningsByDay(tasks, role);
    const { rangeStart, end } = buildWeekGrid(weekCount);

    const activeDays = countActiveDaysInRange(activityByDay, rangeStart, end);
    const totalEvents = sumMapInRange(activityByDay, rangeStart, end);
    const totalEarned = sumMapInRange(earningsByDay, rangeStart, end);

    const submissions = tasks.reduce(
        (sum, task) => sum + (Array.isArray(task.submissions) ? task.submissions.length : 0),
        0,
    );

    return {
        activeDays,
        totalEvents,
        totalEarned,
        submissions,
    };
}
